// In-app notifications inbox. Address-change notifications are written
// server-side (functions/index.js -> onEventAddressChange) to every participant
// of an event, so a guest still finds out the address moved even if they never
// granted push permission. Stored owner-only at users/{uid}/notifications/{id}
// (see firestore.rules); the client reads/marks-read/deletes its own.
//
// A doc looks like:
//   { type: "address_change", code, eventName, addressTitle, address,
//     createdAt: <serverTimestamp>, read: false }
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../Configuration/firebase";

// Cap how many we ever load/render, so a big inbox never costs a big read or an
// endlessly tall dropdown. Older ones are cleaned up by the Firestore TTL policy
// on `notifications.expiresAt` (see onEventAddressChange / BACKLOG.md).
export const NOTIF_LIMIT = 50;

// Live subscription to the user's most recent notifications, newest first.
// onSnapshot (not a collectionGroup query) so it works in the iOS WebView.
// Returns the unsubscribe.
export function subscribeNotifications(uid, callback) {
  const q = query(
    collection(db, "users", uid, "notifications"),
    orderBy("createdAt", "desc"),
    limit(NOTIF_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error("Failed to subscribe to notifications:", err);
      callback([]);
    },
  );
}

export async function markNotificationRead(uid, id) {
  try {
    await updateDoc(doc(db, "users", uid, "notifications", id), { read: true });
  } catch (err) {
    console.error("Failed to mark notification read:", err);
  }
}

// Marks a set of notifications read in one batch. `ids` come from the live list
// the caller already holds, so there's no extra query (and no composite index).
export async function markNotificationsRead(uid, ids) {
  if (!ids || ids.length === 0) return;
  try {
    const batch = writeBatch(db);
    ids.forEach((id) =>
      batch.update(doc(db, "users", uid, "notifications", id), { read: true }),
    );
    await batch.commit();
  } catch (err) {
    console.error("Failed to mark notifications read:", err);
  }
}

export async function deleteNotification(uid, id) {
  try {
    await deleteDoc(doc(db, "users", uid, "notifications", id));
  } catch (err) {
    console.error("Failed to delete notification:", err);
  }
}

// Deletes a set of notifications in one batch. `ids` come from the live list the
// caller holds (at most NOTIF_LIMIT), so "Clear all" clears everything on screen;
// anything older is handled by the TTL policy.
export async function clearNotifications(uid, ids) {
  if (!ids || ids.length === 0) return;
  try {
    const batch = writeBatch(db);
    ids.forEach((id) =>
      batch.delete(doc(db, "users", uid, "notifications", id)),
    );
    await batch.commit();
  } catch (err) {
    console.error("Failed to clear notifications:", err);
  }
}
